import { Truck, Clock, MapPin, CreditCard, Mail, AlertTriangle, Package } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-2">{title}</h2>
        <div className="text-sm text-gray-600 leading-relaxed space-y-1">{children}</div>
      </div>
    </div>
  </div>
);

const ShippingPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Strip */}
      <div className="bg-blue-700 text-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-2">Legal</p>
          <h1 className="text-4xl font-extrabold mb-3">Shipping Policy</h1>
          <p className="text-blue-100">Last updated: April 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12 space-y-5">
        {/* Quick Summary Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <p className="text-sm font-semibold text-blue-800 mb-3 uppercase tracking-wide">Quick Summary</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Processing", value: "1–2 days" },
              { label: "Delivery", value: "3–5 days" },
              { label: "Free shipping", value: "Above ₹999" },
              { label: "COD", value: "Not available" },
            ].map(({ label, value }) => (
              <div key={label} className="text-center">
                <p className="text-lg font-bold text-blue-700">{value}</p>
                <p className="text-xs text-blue-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <Section icon={MapPin} title="Where We Ship">
          <p>
            We ship across <strong>India only</strong>. International shipping is not available at this time.
          </p>
        </Section>

        <Section icon={Clock} title="Processing Time">
          <p>
            All orders are processed within <strong>1–2 business days</strong> after successful payment confirmation.
          </p>
          <p className="mt-1">
            Orders placed on weekends or public holidays are processed on the next business day.
          </p>
        </Section>

        <Section icon={Truck} title="Delivery Time">
          <p>
            Delivery takes <strong>3–5 business days</strong> from the date of dispatch for most pin codes.
          </p>
          <p className="mt-1">
            Remote or Tier-3 locations may take up to <strong>7 business days</strong>. You will receive a
            tracking link via email once your order ships.
          </p>
          <p className="mt-1">
            Total time from order to delivery is typically <strong>4–7 business days</strong>.
          </p>
        </Section>

        <Section icon={Package} title="Shipping Charges">
          <div className="rounded-lg overflow-hidden border border-gray-200 mt-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Order Value</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-700">Shipping Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-600">Below ₹999</td>
                  <td className="px-4 py-3 font-medium text-gray-900">₹79</td>
                </tr>
                <tr className="border-t border-gray-100 bg-green-50">
                  <td className="px-4 py-3 text-gray-600">₹999 and above</td>
                  <td className="px-4 py-3 font-semibold text-green-700">FREE</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={CreditCard} title="Payment Methods">
          <p>
            We currently accept <strong>online payments only</strong> via Razorpay — including UPI,
            credit/debit cards, and net banking.
          </p>
          <p className="mt-1">
            <strong>Cash on Delivery (COD) is not available.</strong>
          </p>
        </Section>

        <Section icon={Mail} title="Order Confirmation & Tracking">
          <p>
            After a successful payment, you'll receive an <strong>email confirmation</strong>. Once dispatched,
            a <strong>tracking link</strong> will be sent to your registered email address.
          </p>
          <p className="mt-1">
            If you don't receive a confirmation email, please check your spam folder. Still nothing?
            Email us at{" "}
            <a href="mailto:support@collegedrip.in" className="text-blue-600 hover:underline font-medium">
              support@collegedrip.in
            </a>{" "}
            with your name and order date.
          </p>
        </Section>

        <Section icon={AlertTriangle} title="Delays & Liability">
          <p>
            CollegeDrip is not responsible for delays caused by courier partners, force majeure events
            (natural disasters, strikes, etc.), or <strong>incorrect delivery addresses</strong> provided
            at checkout.
          </p>
          <p className="mt-1 font-medium text-gray-700">
            Please double-check your delivery address before placing an order.
          </p>
        </Section>

        {/* Footer note */}
        <p className="text-xs text-gray-400 text-center pt-4">
          College Drip is a registered sole proprietorship. Trade Name: College Drip · GSTIN: 29CIGPN5173F1ZZ.
          For questions, contact{" "}
          <a href="mailto:support@collegedrip.in" className="text-blue-500 hover:underline">
            support@collegedrip.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default ShippingPolicyPage;